import type { LaserscanData } from 'hooks/useLaserscanWebSocket'
import { useMemo } from 'react'
import { Layer, Source } from 'react-map-gl'
import { getYawFromQuaternion } from 'utils/getYawFromQuaternion'
import { scanFrameIdFromPayload, scanPoleOffsetMeters } from 'utils/laserscanPole'

type Props = {
  scan: LaserscanData | null
  show: boolean
  robotPosition?: number[] | null
  robotOrientation?: { x: number; y: number; z: number; w: number } | null
  poseFrame?: string | null
  wheelBaseM?: number | null
}

const LaserscanLayer = ({ scan, show, robotPosition, robotOrientation, poseFrame, wheelBaseM }: Props) => {
  const geojson = useMemo(() => {
    if (!scan || !robotPosition || !robotPosition.length) {
      return { type: 'FeatureCollection' as const, features: [] }
    }

    const [robotLng, robotLat] = robotPosition

    // Get the mapbox bearing in degrees
    let mapboxBearing = 0
    if (robotOrientation) {
      mapboxBearing = getYawFromQuaternion(robotOrientation)
    }

    // Convert Mapbox bearing (0=North, 90=East) back to standard math angle (0=East, 90=North)
    const yawRad = (90 - mapboxBearing) * (Math.PI / 180)

    const points: Array<[number, number]> = []
    const { angle_min, angle_increment, range_min, range_max, ranges } = scan
    const pole = scanPoleOffsetMeters({
      poseFrame,
      wheelBaseM,
      scanFrameId: scanFrameIdFromPayload(scan),
    })

    // Approximation for local cartesian to global lat/lng
    const R_EARTH = 6378137 // meters
    const latRad = (robotLat * Math.PI) / 180
    const mPerDegLat = (2 * Math.PI * R_EARTH) / 360
    const mPerDegLng = mPerDegLat * Math.cos(latRad)

    for (let i = 0; i < ranges.length; i++) {
      const r = ranges[i]
      if (r == null || Number.isNaN(r) || !Number.isFinite(r)) continue
      if (r < range_min || r > range_max) continue

      const angle = angle_min + i * angle_increment

      // Ray in the scan frame, then the pole expressed in the pose frame (x forward, y left).
      const localX = pole.x + r * Math.cos(angle)
      const localY = pole.y + r * Math.sin(angle)

      // Rotate by robot yaw to align with global East/North
      const globalEast = localX * Math.cos(yawRad) - localY * Math.sin(yawRad)
      const globalNorth = localX * Math.sin(yawRad) + localY * Math.cos(yawRad)

      // Add to robot WGS84 position
      const pointLng = robotLng + globalEast / mPerDegLng
      const pointLat = robotLat + globalNorth / mPerDegLat

      points.push([pointLng, pointLat])
    }

    return {
      type: 'FeatureCollection' as const,
      features: points.map(point => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: point,
        },
        properties: {},
      })),
    }
  }, [scan, robotPosition, robotOrientation, poseFrame, wheelBaseM])

  if (!show || !scan) return null

  return (
    <Source id="laserscan-source" type="geojson" data={geojson}>
      <Layer
        id="laserscan-layer"
        type="circle"
        source="laserscan-source"
        paint={{
          'circle-radius': 3,
          'circle-color': '#ff0000', // Red color for high visibility
          'circle-opacity': 0.8,
          'circle-stroke-width': 0.3,
          'circle-stroke-color': '#ff0000',
        }}
      />
    </Source>
  )
}

export default LaserscanLayer
